import { RoomInventory, StagingRequestDTO, StagingUpsellInformationDTO } from 'models/virtualStaging'
import { buildStagingUpsellInformation, getGalleryStagingDetails } from 'utils/serialization/galleryStagingPayload'
import { identifiableProduct } from 'utils/serialization/productIdentifier'
import { useCallback, useMemo, useState } from 'react'
import { useRenovationProducts, useStyleSelection } from 'components/common/StagingFlow/main/contexts'

import { APIRequestState } from 'constants/API'
import { AssignmentDTO } from 'models/assignment'
import { AssignmentDTOIsClientDTO } from 'utils/typeguards'
import { AssignmentStage } from 'constants/assignment'
import { Nullable } from 'models/helpers'
import { ProductDTO } from 'models/product'
import { STAGING_PRODUCT_KINDS_PROPERTY_TYPES } from 'constants/product'
import constate from 'constate'
import { logger } from 'utils/logging'
import { useGalleryAssignment } from '../../../../_main/contexts'
import { useGalleryStagingVisualSelection } from './GalleryStagingVisualSelection.context'
import { useRoomAPI } from 'components/contexts/RoomAPI.context'
import { useRoomInventory } from 'components/contexts/RoomInventory.context'
import { useSelectVisualsForStaging } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useStyleSelectionContext } from 'components/common/StagingSelection'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/** Enum of all the staging flow steps. */
export enum GalleryStagingStep {
  STAGING_LIST = 'STAGING_LIST',
  VISUAL_SELECTION = 'VISUAL_SELECTION',
  VISUAL_STAGING = 'VISUAL_STAGING',
}

/** What the upsell payment modal hands back once the client confirmed a renovation purchase. */
export interface GalleryStagingUpsellSelection {
  paymentIntentId?: string
  /** Product2 code of the selected payment option — the only identifier the staging wire accepts. */
  billingProductCode: Nullable<string>
}

/** Staging Flow steps with ascending weights to match intended order. */
export const stagingFlowStepsOrder: Record<GalleryStagingStep, number> = {
  STAGING_LIST: 0,
  VISUAL_SELECTION: 1,
  VISUAL_STAGING: 2,
}

export const [GalleryStagingFlowContextProvider, useGalleryStagingFlow] = constate(() => {

  const { t } = useTranslation(['gallery_staging_flow'])
  const { spawnErrorToast } = useSnackbar()
  const { userPropertyWorkspaceId } = useUserData()
  const { setActiveRoomIndex } = useStyleSelectionContext()
  const { selectedVisuals, setSelectedVisuals } = useGalleryStagingVisualSelection()
  const { createRoomObject, setRooms, roomInventory } = useRoomInventory()
  const { assignRoomToImage, unassignAll, allRoomsCompleted } = useRoomAPI()
  const { getRoomsWithRenovationProducts, renovationCartProduct } = useRenovationProducts()
  const { assignmentData } = useGalleryAssignment()
  const { allRooms } = useStyleSelection()

  const selectVisualsForStagingMutation = useSelectVisualsForStaging()

  const [stagingAssignmentId, setStagingAssignmentId] = useState<string | null>(null)
  const [assignmentsToStage, setAssignmentsToStage] = useState<AssignmentDTO[]>([])
  const [stagingProduct, setStagingProduct] = useState<ProductDTO | null>(null)
  const [activeStep, setActiveStep] = useState<GalleryStagingStep>(GalleryStagingStep.VISUAL_SELECTION)
  const [isAdvanceStagingPurchaseModalOpen, setIsAdvanceStagingPurchaseModalOpen] = useState<boolean>(false)

  const stagingRequestState = useMemo(() => {
    if (selectVisualsForStagingMutation.isPending) return APIRequestState.RUNNING
    if (selectVisualsForStagingMutation.isSuccess) return APIRequestState.OK
    if (selectVisualsForStagingMutation.isError) return APIRequestState.ERROR
    return APIRequestState.BEFORE_START
  }, [selectVisualsForStagingMutation.isPending, selectVisualsForStagingMutation.isSuccess, selectVisualsForStagingMutation.isError])

  /** Remaining assignments with stagings which are not finished */
  const remainingStagingsCount = useMemo(() => {
    const unfinishedStagingAssignments = assignmentsToStage.filter(assignment =>
      assignment.stage === AssignmentStage.MISSION_ORDER_PLACED || assignment.stage === AssignmentStage.PRE_PRODUCTION
    )
    return unfinishedStagingAssignments.length
  }, [assignmentsToStage])

  /** Derives how many visuals have to be selected from staging product quantity */
  const requiredSelectionCount = useMemo(() => stagingProduct ? stagingProduct.quantity : 0, [stagingProduct])

  /** Returns the rooms ids with renovation products included */
  const roomsWithRenovationProducts = useMemo(() => getRoomsWithRenovationProducts(allRooms), [allRooms, getRoomsWithRenovationProducts])

  /**
   * Generates one room for each selected image.
   * Assigns room key of visual id and property type according to staging product.
   * Pushes rooms to inventory and assigns images to rooms 
   */
  const generateRoomsForSelectedImages = useCallback(() => {
    if (!stagingProduct) return false

    const rooms: RoomInventory = {}

    for (const visual of Object.values(selectedVisuals)) {

      if (!visual) continue

      rooms[visual.id] = roomInventory[visual.id] || createRoomObject(stagingProduct.id, {
        key: visual.id,
        propertyType: STAGING_PRODUCT_KINDS_PROPERTY_TYPES[stagingProduct.kind]
      })

      assignRoomToImage(visual.id, visual.id)
    }

    setRooms(rooms)

    return true
  }, [assignRoomToImage, createRoomObject, roomInventory, selectedVisuals, setRooms, stagingProduct])

  /** Resets states of image selection and staging */
  const cleanupStaging = () => {
    setStagingProduct(null)
    setStagingAssignmentId(null)
    setSelectedVisuals({})
    unassignAll()
    setRooms({})
    setActiveRoomIndex(0)
    setAssignmentsToStage([])
    setActiveStep(GalleryStagingStep.VISUAL_SELECTION)
    setIsAdvanceStagingPurchaseModalOpen(false)

    selectVisualsForStagingMutation.reset()
  }

  /** Sets the staging product and stagingAssignmentId */
  const initializeStaging = (stagingAssignmentId: string, stagingProduct: ProductDTO) => {
    setStagingAssignmentId(stagingAssignmentId)
    setStagingProduct(stagingProduct)
  }

  /** Serializes staging configuration information into payload */
  const getStagingConfig = useCallback((upsellInformation?: StagingUpsellInformationDTO): StagingRequestDTO | undefined => {

    return {
      details: getGalleryStagingDetails(allRooms),
      upsellInformation,
    }

  }, [allRooms])

  /**
   * Completes the current staging.
   *
   * Every exit below leaves `selectVisualsForStagingMutation` untouched, so the upsell modal keeps
   * `beforeConfirmActionState` at BEFORE_START and never confirms the Stripe payment intent. That is
   * what makes an abort safe after the intent has been created (ISSUE-3512).
   */
  const onStageVisuals = useCallback((upsellInformation?: GalleryStagingUpsellSelection) => {
    /**
     * Logs which precondition stopped the staging, so an unreproducible "nothing happened" is one
     * Sentry search. An error-level abort also tells the client, who has just entered payment
     * details; the warn-level ones are ordinary "not ready yet" states and stay silent.
     */
    const abort = (reason: string, level: 'warn' | 'error' = 'warn') => {
      if (level === 'error') spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))

      logger[level](`Gallery staging not submitted - ${reason}`, undefined, {
        context: {
          component: 'GalleryStagingFlow',
          action: 'onStageVisuals',
          metadata: {
            reason,
            stagingAssignmentId,
            assignmentId: assignmentData?.id,
            activeStep,
            allRoomsCompleted,
            hasUpsell: !!upsellInformation,
            paymentIntentId: upsellInformation?.paymentIntentId,
          },
        },
      })
    }

    if (!assignmentData) return abort('missing assignment data')
    if (!AssignmentDTOIsClientDTO(assignmentData)) return abort('assignment is not a client DTO')
    if (activeStep !== GalleryStagingStep.VISUAL_STAGING) return abort('wrong step')
    if (!allRoomsCompleted) return abort('rooms incomplete')
    if (!stagingAssignmentId) return abort('missing stagingAssignmentId')

    let stagingConfig = null

    if (upsellInformation) {
      // The payment intent already exists at this point, so a renovation product that vanished from
      // a background refetch must abort rather than post a staging stripped of the upsell block.
      if (!renovationCartProduct) return abort('renovation product missing after payment intent', 'error')

      // Both products reach the wire as a Product2 code and nothing else, so one without a code
      // aborts here rather than posting an upsell block the API would reject.
      const renovationProduct = identifiableProduct(renovationCartProduct.productCode)
      const billingProduct = identifiableProduct(upsellInformation.billingProductCode)

      if (!renovationProduct) return abort('renovation product carries no product code', 'error')
      if (!billingProduct) return abort('billing product carries no product code', 'error')

      stagingConfig = getStagingConfig(buildStagingUpsellInformation({
        renovationProduct,
        billingProduct,
        paymentIntentId: upsellInformation.paymentIntentId,
        correlation: { stagingAssignmentId, assignmentId: assignmentData.id },
      }))
    } else {
      stagingConfig = getStagingConfig()
    }
    if (!stagingConfig) return abort('no staging config')

    selectVisualsForStagingMutation.mutate({
      assignmentId: assignmentData.id,
      dealId: assignmentData.dealId,
      workspaceId: userPropertyWorkspaceId,
      stagingAssignmentId,
      stagingConfiguration: stagingConfig,
    })
  }, [activeStep, allRoomsCompleted, assignmentData, getStagingConfig, renovationCartProduct, selectVisualsForStagingMutation, spawnErrorToast, stagingAssignmentId, t, userPropertyWorkspaceId])

  // NAVIGATION
  const stagingSteps = useMemo(() => {
    const defaultSteps = [GalleryStagingStep.VISUAL_SELECTION, GalleryStagingStep.VISUAL_STAGING]
    if (assignmentsToStage.length > 0) defaultSteps.push(GalleryStagingStep.STAGING_LIST)

    return defaultSteps.sort((aStep, bStep) => stagingFlowStepsOrder[aStep] - stagingFlowStepsOrder[bStep])
  }, [assignmentsToStage.length])
  const currentStepIndex = useMemo(() => stagingSteps.findIndex(step => step === activeStep), [activeStep, stagingSteps])
  const isLastStep = useMemo(() => activeStep === GalleryStagingStep.VISUAL_STAGING, [activeStep])

  const goNext = useCallback(() => {
    const nextStep = stagingSteps[currentStepIndex + 1]
    if (nextStep === GalleryStagingStep.VISUAL_STAGING) {
      generateRoomsForSelectedImages()
    }
    setActiveStep(stagingSteps[currentStepIndex + 1])
  }, [currentStepIndex, generateRoomsForSelectedImages, stagingSteps])

  const goBack = useCallback(() => {
    setActiveStep(stagingSteps[currentStepIndex - 1])
  }, [currentStepIndex, stagingSteps])

  const isVisualSelectionValid = useMemo(() => {
    return requiredSelectionCount === Object.values(selectedVisuals).length
  }, [requiredSelectionCount, selectedVisuals])

  const isVisualStagingValid = useMemo(() => {
    return allRoomsCompleted
  }, [allRoomsCompleted])

  const stepValidityMap = useMemo<Record<GalleryStagingStep, boolean>>(() => ({
    [GalleryStagingStep.STAGING_LIST]: false, // No need to enable next step on the staging list step
    [GalleryStagingStep.VISUAL_SELECTION]: isVisualSelectionValid,
    [GalleryStagingStep.VISUAL_STAGING]: isVisualStagingValid
  }), [isVisualSelectionValid, isVisualStagingValid])

  return {
    allRooms,
    activeStep,
    stagingProduct,
    requiredSelectionCount,
    stagingAssignmentId,
    stagingSteps,
    currentStepIndex,
    stepValidityMap,
    isLastStep,
    assignmentsToStage,
    roomsWithRenovationProducts,
    isAdvanceStagingPurchaseModalOpen,
    remainingStagingsCount,
    stagingRequestState,
    selectVisualsForStagingMutation,
    setActiveStep,
    setAssignmentsToStage,
    getStagingConfig,
    generateRoomsForSelectedImages,
    initializeStaging,
    cleanupStaging,
    onStageVisuals,
    setIsAdvanceStagingPurchaseModalOpen,
    goNext,
    goBack,
  }
})
